/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { cva } from "@panda/css";
import { Box } from "@panda/jsx";
import { Fragment } from "react";
import type { ReactNode } from "react";

import { ResponsiveContainer } from "../../atoms/container/ResponsiveContainer.atom";
import { SectionContainer } from "../../atoms/container/SectionContainer.atom";

export interface DirectorySpotlightProps<T> {
	/**
	 * Items to render in the spotlight grid. Pass exactly
	 * `DEFAULT_SPOTLIGHT_COUNT` (10) from `@cosmos/airtable-data` so the
	 * desktop layout fills a balanced 3×3 — the 10th card is hidden on
	 * desktop via the layout below.
	 */
	items: T[];
	/** Render-prop for each card. Caller wires the domain card adapter. */
	renderCard: (item: T) => ReactNode;
	/** Stable React key for each item. */
	getKey: (item: T) => string;
}

/**
 * Same responsive 3 → 2 → 1 layout the main `DirectoryGrid` uses for the
 * canonical Cosmos card-section pattern. Keeping it local so Spotlight's
 * content edges align exactly with the main grid below it.
 *
 * The `:nth-child(10)` rule pairs with `DEFAULT_SPOTLIGHT_COUNT` in
 * `@cosmos/airtable-data/spotlight` — at desktop's 3-col grid the 10th
 * card would orphan a row, so we hide it for a balanced 3×3. Tablet's
 * 2-col grid (5 rows) and mobile's 1-col grid (10 rows) both fill cleanly.
 */
const spotlightGridStyles = cva({
	base: {
		display: "grid",
		gap: "eds.dimension.24",
		width: "full",
		// Same breakpoint conventions as `DirectoryGrid`: 3 cols at ≥lg
		// (1280+), 2 cols at tablet (smOnly..lgDown), 1 col at ≤sm mobile.
		gridTemplateColumns: { base: "3", lgDown: "2", smDown: "1" },
		"& > *:nth-child(10)": {
			display: { base: "none", lgDown: "block" },
		},
	},
});

/**
 * Generic "weekly spotlight" strip — a responsive 3 → 2 → 1 card grid that
 * renders between an editor-placed heading section and the main directory
 * grid. Domain-agnostic: pass `items` + a `renderCard` adapter for the
 * concrete card type.
 *
 * Pass `DEFAULT_SPOTLIGHT_COUNT` items so the desktop 10th-card-hide rule
 * lines up with the layout.
 *
 * Returns `null` for empty input so consumers can render it unconditionally
 * (the variant gate happens upstream).
 */
export function DirectorySpotlight<T>({
	items,
	renderCard,
	getKey,
}: DirectorySpotlightProps<T>): React.JSX.Element | null {
	if (items.length === 0) return null;

	return (
		<ResponsiveContainer>
			{/*
				The editor-placed "Weekly Spotlight" heading section sits directly above
				this container. Replacing the default section block-padding with a tight
				32px gap so the heading and spotlight cards read as one connected unit
				without crowding.
			*/}
			<SectionContainer disablePaddingBlockStart paddingBlockStart="eds.dimension.32">
				<Box className={spotlightGridStyles()}>
					{items.map((item) => (
						<Fragment key={getKey(item)}>{renderCard(item)}</Fragment>
					))}
				</Box>
			</SectionContainer>
		</ResponsiveContainer>
	);
}
